"use client";

/**
 * useUrlImport — YouTube/Vimeo/Cobalt URL validation and import.
 */

import { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";
import {
  isBlockedUrl,
  detectUrlMediaType,
  buildCobaltPattern,
  buildPlatformIcons,
} from "../url-validators";
import { URL_IMPORT_MAX_SIZE, MAX_DURATION_SECONDS } from "../supported-formats";
import { calculateMediaCost } from "../cost-calculator";

interface ResolvedMediaItem {
  resolvedUrl: string;
  filename: string;
  duration: number;
  size: number;
  mediaType: string;
}

interface ResolveUrlResponse {
  items: ResolvedMediaItem[];
}

interface PlatformsResponse {
  platforms: string[];
}

export interface UrlImportItem {
  id: string;
  originalUrl: string;
  downloadUrl: string;
  fileName: string;
  description: string;
  mediaCategory: "audio" | "video";
  mediaType: string;
  duration: number;
  amount: number;
  size: number;
  folderId: string;
  sourceLanguage: string;
  /** Target language for translate mode. Empty until user picks one. */
  targetLanguage: string;
  /** Per-row dubbing toggle for translate mode. */
  enableDubbing: boolean;
  isUploaded?: boolean;
}

/**
 * @param insightCost - per-second insight pricing used to compute upload amount
 * @param defaultTargetLanguage - when non-empty, new URL items pre-fill targetLanguage
 *   with this code. Defaults to "" (no pre-fill). Caller passes the translate-mode
 *   first-translatable-language fallback so URL items match file items at submit.
 */
export function useUrlImport(insightCost: number, defaultTargetLanguage: string = "") {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cobaltPattern, setCobaltPattern] = useState<RegExp | null>(null);

  const { data: platforms } = useQuery({
    queryKey: ["upload", "supported-platforms"],
    queryFn: async () => {
      const res = await apiClient.get<PlatformsResponse>(
        "/media/supported-platforms"
      );
      if (res.success && res.data) return res.data.platforms;
      return [];
    },
    staleTime: 30 * 60 * 1000,
  });

  useEffect(() => {
    if (platforms && platforms.length > 0) {
      setCobaltPattern(buildCobaltPattern(platforms));
    }
  }, [platforms]);

  const platformIcons = platforms ? buildPlatformIcons(platforms) : [];

  const validateUrl = useCallback(
    (url: string): { isValid: boolean; mediaType: "audio" | "video" | null } => {
      const mediaType = detectUrlMediaType(url, cobaltPattern);
      return { isValid: mediaType !== null, mediaType };
    },
    [cobaltPattern]
  );

  const importUrl = useCallback(
    async (
      url: string,
      folderId: string,
      sourceLanguage: string
    ): Promise<{
      items: UrlImportItem[];
      error?: string;
      downloadFallback?: { url: string; title: string };
    }> => {
      // SSRF guard — block private/reserved hosts before the server call.
      if (isBlockedUrl(url)) {
        setError(t("UPLOAD_FILE.URL_INVALID"));
        return { items: [] };
      }

      setIsLoading(true);
      setError(null);

      try {
        const res = await apiClient.post<ResolveUrlResponse>(
          "/media/resolve",
          { url }
        );

        if (!res.success || !res.data) {
          const errMsg = res.error || t("UPLOAD_FILE.URL_NO_MEDIA");
          setError(errMsg);
          return { items: [], error: errMsg };
        }

        const { items } = res.data;

        if (!items || items.length === 0) {
          const errMsg = t("UPLOAD_FILE.URL_NO_MEDIA");
          setError(errMsg);
          return { items: [], error: errMsg };
        }

        const ALLOWED_MEDIA_TYPES = new Set(["audio", "video"]);
        const mediaItems = items
          .filter((item) => item.mediaType !== "photo")
          .filter((item) => ALLOWED_MEDIA_TYPES.has(item.mediaType));

        if (mediaItems.length === 0) {
          const errMsg = t("UPLOAD_FILE.URL_NO_MEDIA");
          setError(errMsg);
          return { items: [], error: errMsg };
        }

        const importItems: UrlImportItem[] = [];

        for (const item of mediaItems) {
          if (item.duration > MAX_DURATION_SECONDS) {
            setError(t("UPLOAD_FILE.URL_TOO_LONG"));
            continue;
          }

          // Too large for direct import — offer the download fallback instead.
          if (item.size > URL_IMPORT_MAX_SIZE) {
            const fallbackUrl = item.resolvedUrl.slice(0, 2048);
            let isSecure = false;
            try { isSecure = new URL(fallbackUrl).protocol === "https:"; } catch { /* invalid = blocked */ }
            return {
              items: [],
              downloadFallback: isSecure
                ? { url: fallbackUrl, title: item.filename || "Untitled" }
                : undefined,
              error: t("UPLOAD_FILE.URL_TOO_LARGE"),
            };
          }

          importItems.push({
            id: `url-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            originalUrl: url,
            downloadUrl: item.resolvedUrl,
            fileName: (item.filename || "Untitled").slice(0, 200),
            description: "",
            mediaCategory: item.mediaType === "audio" ? "audio" : "video",
            mediaType: item.mediaType,
            duration: item.duration || 0,
            amount: calculateMediaCost(item.duration || 0, insightCost),
            size: item.size || 0,
            folderId,
            sourceLanguage,
            targetLanguage: defaultTargetLanguage,
            enableDubbing: false,
            isUploaded: false,
          });
        }

        return { items: importItems };
      } catch {
        const errMsg = t("UPLOAD_FILE.URL_NO_MEDIA");
        setError(errMsg);
        return { items: [], error: errMsg };
      } finally {
        setIsLoading(false);
      }
    },
    [insightCost, t, defaultTargetLanguage]
  );

  return {
    isLoading,
    error,
    platformIcons,
    validateUrl,
    // True once the server's supported-platform list has resolved into a pattern.
    // Callers gate pre-flight URL validation on this so URLs aren't rejected
    // before the Cobalt platform list loads.
    platformsLoaded: cobaltPattern !== null,
    importUrl,
    clearError: () => setError(null),
  };
}
